import React, { useEffect, useState } from "react";
import { Table, Tag, Modal, Space, Input, Typography, Row, Col } from "antd";
import { PictureOutlined, FilePdfOutlined, FileOutlined, MinusCircleOutlined } from "@ant-design/icons";
import ApiSelect from "./ApiSelect";
import { linyuanApi } from "../../common/javascripts/common.js";
import "./DataBank.css";

const pageSize = 10;

const fixedDocTitles = [
    "TDS",
    "COA",
    "SDS",
    "包装照",
    "产品照",
];

function truncateTitle(title) {
    if (!title) return "";
    return title.length > 8 ? `${title.slice(0, 5)}…` : title;
}

export default function Databank(props) {
    const { lang = 0 } = props;
    const [grades, setGrades] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [productFilter, setProductFilter] = useState("");
    const [companyFilter, setCompanyFilter] = useState("");
    const [brandFilter, setBrandFilter] = useState("");
    const [keyword, setKeyword] = useState("");
    const [detailVisible, setDetailVisible] = useState(false);
    const [detailRecord, setDetailRecord] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState("");
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        
        return () => {
            window.removeEventListener('resize', checkIsMobile);
        };
    }, []);

    useEffect(() => {
        if (productFilter) {
            handleSearch();
        }
    }, [productFilter]);

    useEffect(() => {
        // loadData(1);
    }, []);

    const loadData = async (page, extraParams = {}) => {
        try {
            setLoading(true);
            const params = {};
            params.currentPage = page;
            params.pageSize = pageSize;
            if (keyword) {
                params.search = keyword;
            }
            // Use explicit productId if provided (to handle async state updates), otherwise use state
            const activeProductId = extraParams.productId !== undefined ? extraParams.productId : productFilter;
            if (activeProductId) {
                params.productId = activeProductId;
            }
            if (companyFilter) {
                params.companyIds = companyFilter;
            }
            const res = await linyuanApi.get("/grades", { params });
            const apiData = res || {};
            const pageData = apiData.data || apiData;
            setGrades(pageData.list || []);
            setTotal(pageData.totalCount || 0);
            setCurrentPage(pageData.currentPage || page);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        loadData(1);
    };

    const handlePageChange = (page) => {
        loadData(page);
    };

    const openDetail = (record) => {
        setDetailRecord(record);
        setDetailVisible(true);
    };

    const getDocTypeInfo = (doc) => {
        const href = doc && doc.uri;
        const type = doc && doc.type;
        if (!href) {
            return {
                kind: "empty",
                icon: <MinusCircleOutlined />,
            };
        }
        if (type === 1) {
            return {
                kind: "image",
                icon: <PictureOutlined />,
            };
        }
        if (type === 2) {
            return {
                kind: "pdf",
                icon: <FilePdfOutlined />,
            };
        }
        return {
            kind: "other",
            icon: <FileOutlined />,
        };
    };

    const buildDocGroups = (docs) => {
        const source = Array.isArray(docs) ? docs.slice() : [];
        const remaining = source.slice();
        const regularDocs = fixedDocTitles.map((title) => {
            const lower = title.toLowerCase();
            const idx = remaining.findIndex((item) => {
                const t = (item.title || "").trim().toLowerCase();
                return t === lower;
            });
            if (idx === -1) {
                return {
                    key: title,
                    title,
                    uri: null,
                };
            }
            const doc = remaining[idx];
            remaining.splice(idx, 1);
            return {
                key: doc.document_id || doc.id || `${doc.grade_id}-${doc.uri}`,
                ...doc,
                title,
            };
        });
        const otherDocs = remaining.map((item) => ({
            key: item.document_id || item.id || `${item.grade_id}-${item.uri}`,
            ...item,
        }));
        return {
            regularDocs,
            otherDocs,
        };
    };

    const buildDownloadUrl = (uri, name) => {
        if (!uri) {
            return uri;
        }
        if (uri.indexOf("attname=") !== -1) {
            return uri;
        }
        const fileName = name || "";
        const hasQuery = uri.indexOf("?") !== -1;
        const sep = hasQuery ? "&" : "?";
        const encoded = encodeURIComponent(fileName);
        return `${uri}${sep}attname=${encoded}`;
    };

    const DocTag = ({ doc }) => {
        const title = (doc && (doc.title || doc.uri)) || "";
        const href = doc && doc.uri;
        const key = doc && doc.key;
        const { kind, icon } = getDocTypeInfo(doc);
        const disabled = kind === "empty";

        const tag = (
            <Tag
                className={`databank-documents-badge ${
                    disabled ? "databank-documents-badge-disabled" : ""
                }`}
                color={disabled ? "default" : "blue"}
            >
                <span className="databank-documents-icon">{icon}</span>
                {truncateTitle(title)}
            </Tag>
        );

        if (disabled) {
            return (
                <span
                    key={key}
                    className="databank-documents-badge-link disabled"
                    title={title}
                >
                    {tag}
                </span>
            );
        }

        if (kind === "image") {
            return (
                <span
                    key={key}
                    className="databank-documents-badge-link"
                    title={title}
                    onClick={() => {
                        if (!href) {
                            return;
                        }
                        setPreviewImage(href);
                        setPreviewVisible(true);
                    }}
                >
                    {tag}
                </span>
            );
        }

        return (
            <a
                key={key}
                href={buildDownloadUrl(href, title)}
                download
                className="databank-documents-badge-link"
                title={title}
                target="_blank"
            >
                {tag}
            </a>
        );
    };

    const columns = [
        {
            title: lang === 0 ? "牌号 / 产品" : "Grade / Product",
            key: "grade_product",
            width: 120,
            render: (_, record) => (
                <div className="databank-grade-product">
                    <div className="databank-grade-text" style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => openDetail(record)}>
                        {record.name || "-"}
                    </div>
                    <div className="databank-product-text">
                        {record.product_alias || "-"}
                    </div>
                </div>
            ),
        },
        {
            title: lang === 0 ? "公司 / 品牌" : "Company / Brand",
            key: "company_brand",
            render: (_, record) => {
                const company = record.company_alias || "-";
                const brand = record.brand_alias || "-";
                const logoUrl = record.company_logo_url;
                const initials = (record.company_alias || "").charAt(0) || "-";
                return (
                    <div className="databank-company-brand-cell">
                        <div className="databank-logo-wrapper">
                            {logoUrl ? (
                                <img
                                    src={logoUrl}
                                    alt={company}
                                    className="databank-logo-image"
                                />
                            ) : (
                                <div className="databank-logo-placeholder">
                                    {initials}
                                </div>
                            )}
                        </div>
                        {!isMobile && (
                            <div className="databank-company-brand-text">
                                <div className="databank-company-text">{company}</div>
                                <div className="databank-brand-text">{brand}</div>
                            </div>
                        )}
                    </div>
                );
            },
        },
        ...(!isMobile ? [{
            title: lang === 0 ? "特性" : "Features",
            key: "features",
            width: 200,
            render: (_, record) => {
                const features = Array.isArray(record.features) ? record.features.slice(0, 3) : [];
                return (
                    <div className="databank-features-cell">
                        {features.map((feature, index) => (
                            <div key={index} className="databank-feature-item">
                                <span className="databank-parameter-text">{lang === 0 ? feature.nameCn : feature.nameEn}: </span>
                                <span className="databank-parameter-data">{feature.parameter || "-"}</span>
                                <span className="databank-parameter-text">{" " + feature.unit || "-"}</span>
                            </div>
                        ))}
                        {features.length < 3 && Array.from({ length: 3 - features.length }).map((_, index) => (
                            <div key={index + features.length} className="databank-feature-item"></div>
                        ))}
                    </div>
                );
            },
        }] : []),
        {
            title: lang === 0 ? "资料" : "Documents",
            dataIndex: "documents",
            key: "documents",
            width: isMobile ? 300 : 600,
            render: (docs) => {
                const list = Array.isArray(docs) ? docs : [];
                const { regularDocs, otherDocs } = buildDocGroups(list);
                return (
                    <div className="databank-documents-cell">
                        <div className="databank-documents-sections">
                            <div className="databank-documents-section">
                                <span className="databank-documents-section-label">
                                    {lang === 0 ? "常规档案" : "Regular"}
                                </span>
                                <div className="databank-documents-list">
                                    {regularDocs.map((d) => (
                                        <DocTag key={d.key} doc={d} />
                                    ))}
                                </div>
                            </div>
                            <div className="databank-documents-section">
                                <span className="databank-documents-section-label">
                                    {lang === 0 ? "其他档案" : "Others"}
                                </span>
                                <div className="databank-documents-list">
                                    {otherDocs.map((d) => {
                                        const href = d.uri;
                                        if (!href) {
                                            return null;
                                        }
                                        return <DocTag key={d.key} doc={d} />;
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="databank-container">
            <div className="databank-header">
                <Typography.Title level={2}>
                    {lang === 0 ? "产品档案库" : "Product Document Bank"}
                </Typography.Title>
                <Typography.Paragraph>
                    {lang === 0
                        ? "根据产品、公司和牌号查询对应的技术资料与生产资料。"
                        : "Search technical and production documents by product, company and grade."}
                </Typography.Paragraph>
            </div>
            <div className="databank-filters">
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <ApiSelect
                            apiUrl="/products"
                            labelKey="alias"
                            valueKey="id"
                            pageSize={pageSize}
                            placeholder={
                                lang === 0 ? "按产品筛选" : "Select by products"
                            }
                            allowClear={false}
                            value={productFilter}
                            onChange={setProductFilter}
                            onOptionsLoaded={(opts) => {
                                if (!productFilter && opts && opts.length > 0) {
                                    setProductFilter(opts[0].value);
                                }
                            }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <ApiSelect
                            apiUrl="/companies"
                            labelKey="alias"
                            searchKey="search"
                            valueKey="id"
                            pageSize={pageSize}
                            placeholder={
                                lang === 0 ? "按生产单位筛选" : "Select by manufacturers"
                            }
                            onChange={setCompanyFilter}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Input
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            placeholder={lang === 0 ? "按牌号名称搜索" : "Search by grade name"}
                            allowClear
                        />
                    </Col>
                    <Col xs={24}>
                        <Space>
                            <a className="databank-search-button" onClick={handleSearch}>
                                {lang === 0 ? "查询" : "Search"}
                            </a>
                        </Space>
                    </Col>
                </Row>
            </div>
            <Table
                rowKey={(record) => record.id}
                columns={columns}
                dataSource={grades}
                loading={loading}
                pagination={{
                    current: currentPage,
                    pageSize,
                    total,
                    onChange: handlePageChange,
                }}
            />
            <Modal
                open={detailVisible}
                onCancel={() => setDetailVisible(false)}
                onOk={() => setDetailVisible(false)}
                width={800}
                title={
                    detailRecord
                        ? `${detailRecord.product_alias || ""} / ${detailRecord.brand_alias || ""} / ${detailRecord.name || ""
                        }`
                        : lang === 0
                            ? "详情"
                            : "Detail"
                }
            >
                {detailRecord && (
                    <div className="databank-detail">
                        <Row gutter={16}>
                            <Col span={12}>
                                {(() => {
                                    const logoUrl = detailRecord.company_logo_url;
                                    const companyName = detailRecord.company_alias || "-";
                                    const brandName = detailRecord.brand_alias || "-";
                                    const companyBrandText =
                                        companyName && brandName
                                            ? `${companyName} | ${brandName}`
                                            : companyName || brandName || "-";
                                    return (
                                        <>
                                            <div className="databank-detail-field">
                                                <span className="databank-detail-label">
                                                    {lang === 0 ? "公司" : "Company"}
                                                </span>
                                                <span className="databank-detail-value">
                                                    {logoUrl && (
                                                        <img
                                                            src={logoUrl}
                                                            alt={companyName}
                                                            className="databank-detail-logo-image"
                                                        />
                                                    )}
                                                </span>
                                            </div>
                                            <div className="databank-detail-field">
                                                <span className="databank-detail-label">
                                                    {lang === 0 ? "品牌" : "Brand"}
                                                </span>
                                                <span className="databank-detail-value">
                                                    <span className="databank-detail-company-brand-text">
                                                        {companyBrandText}
                                                    </span>
                                                </span>
                                            </div>
                                        </>
                                    );
                                })()}
                            </Col>
                            <Col span={12}>
                                <div className="databank-detail-field">
                                    <span className="databank-detail-label">
                                        {lang === 0 ? "牌号" : "Grade"}
                                    </span>
                                    <span className="databank-detail-value">
                                        {detailRecord.name || "-"}
                                    </span>
                                </div>
                                <div className="databank-detail-field">
                                    <span className="databank-detail-label">
                                        {lang === 0 ? "产品" : "Product"}
                                    </span>
                                    <span className="databank-detail-value">
                                        {detailRecord.product_alias || "-"}
                                    </span>
                                </div>
                            </Col>
                        </Row>
                        <div className="databank-detail-features" style={{ marginTop: '20px' }}>
                            <div className="databank-detail-features-header">
                                <span className="databank-detail-features-title">
                                    {lang === 0 ? "牌号特性" : "Grade Features"}
                                </span>
                            </div>
                            <div className="databank-detail-features-table-wrapper" style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '10px' }}>
                                <table className="databank-detail-features-table">
                                    <thead>
                                        <tr>
                                            <th>{lang === 0 ? "特性名称" : "Feature Name"}</th>
                                            <th>{lang === 0 ? "参数" : "Parameter"}</th>
                                            <th>{lang === 0 ? "标准" : "Standard"}</th>
                                            <th>{lang === 0 ? "备注" : "Remark"}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(detailRecord.features) ? detailRecord.features.map((feature, index) => (
                                            <tr key={index}>
                                                <td>{lang === 0 ? feature.nameCn : feature.nameEn}</td>
                                                <td>{feature.parameter || "-"} {feature.unit || ''}</td>
                                                <td>{feature.standard || "-"}</td>
                                                <td>{feature.remark || "-"}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan={4} style={{ textAlign: 'center' }}>{lang === 0 ? "无特性信息" : "No feature information"}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="databank-detail-documents" style={{ marginTop: '20px' }}>
                            <div className="databank-detail-documents-header">
                                    <span className="databank-detail-documents-title">
                                        {lang === 0 ? "全部档案" : "Documents"}
                                        {`(${Array.isArray(detailRecord.documents) ? detailRecord.documents.length : 0}):`}
                                    </span>
                            </div>
                            {(() => {
                                const { regularDocs, otherDocs } = buildDocGroups(detailRecord.documents);
                                return (
                                    <div className="databank-detail-documents-list-wrapper">
                                        <div className="databank-detail-documents-group">
                                            <span className="databank-detail-documents-group-label">
                                                {lang === 0 ? "常规档案" : "Regular"}
                                            </span>
                                            <div className="databank-detail-documents-list">
                                                {regularDocs.map((d) => (
                                                    <DocTag key={d.key} doc={d} />
                                                ))}
                                            </div>
                                        </div>
                                        <div className="databank-detail-documents-group">
                                            <span className="databank-detail-documents-group-label">
                                                {lang === 0 ? "其他档案" : "Others"}
                                            </span>
                                            <div className="databank-detail-documents-list">
                                                {otherDocs.map((d) => {
                                                    const href = d.uri;
                                                    if (!href) {
                                                        return null;
                                                    }
                                                    return <DocTag key={d.key} doc={d} />;
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}
            </Modal>
            <Modal
                open={previewVisible}
                footer={null}
                onCancel={() => {
                    setPreviewVisible(false);
                    setPreviewImage("");
                }}
                width={800}
                bodyStyle={{ textAlign: "center" }}
            >
                {previewImage && (
                    <img
                        src={previewImage}
                        alt={lang === 0 ? "文档预览" : "Preview"}
                        className="databank-preview-image"
                    />
                )}
            </Modal>
        </div>
    );
}
